<template>
  <div class="licenses-manage">
    <div class="mb-2 d-block d-md-flex justify-content-end">
      <h3 v-if="title" class="mb-0 mr-auto">{{ title }}</h3>
      <b-input
        v-if="!title"
        type="search"
        v-model="licensesFilter"
        placeholder="Search licenses..."
        size="sm"
      />
      <b-btn
        v-if="hasPermission('license.delete')"
        :disabled="!licensesSelected.length"
        class="ml-2 shrink-0"
        size="sm"
        variant="danger"
        @click="batchDelete"
      >
        <i class="icon-trash"></i>
        Batch Delete
      </b-btn>
      <b-btn
        v-if="hasPermission('license.edit')"
        :disabled="!licensesSelected.length"
        class="ml-2 shrink-0"
        size="sm"
        variant="primary"
        @click="batchConfigure"
      >
        <i class="icon-settings"></i>
        Batch Config
      </b-btn>
      <b-btn
        v-if="hasPermission('license.assign')"
        :disabled="!licensesSelected.length"
        class="ml-2 shrink-0"
        size="sm"
        variant="primary"
        @click="batchDownload"
      >
        <i class="icon-cloud-download"></i>
        Batch Download
      </b-btn>
      <b-btn
        v-if="!isAdmin || hasPermission('license.assign')"
        class="ml-2 shrink-0"
        size="sm"
        variant="primary"
        @click="toggleEdit"
      >
        <i class="icon-people"></i>
        {{ isEditing ? "Done" : "Assign Users" }}
      </b-btn>
      <b-btn
        v-if="!isAdmin && addLicense"
        :to="{ name: 'Add License' }"
        class="ml-2 shrink-0"
        size="sm"
        variant="primary"
      >
        <i class="icon-plus"></i> Add License
      </b-btn>
    </div>

    <b-input
      v-if="title"
      type="search"
      v-model="licensesFilter"
      placeholder="Search licenses..."
      class="mb-2"
      size="sm"
    />

    <nav>
      <b-pagination
        v-if="licenseTotalFiltered > perPage"
        v-model="licenseCurrentPage"
        :total-rows="licenseTotalFiltered"
        :per-page="perPage"
        align="center"
        prev-text="Prev"
        next-text="Next"
        hide-goto-end-buttons
      />
    </nav>
    <b-table
      :items="licensesTransformed"
      :fields="licenseFieldsComputed"
      :current-page="licenseCurrentPage"
      :per-page="perPage"
      :filter="licensesFilter"
      @filtered="onFiltered"
      sort-by="status"
      responsive="lg"
      striped
    >
      <template #head(select)>
        <b-checkbox
          v-model="allLicensesSelected"
          :indeterminate="licensesSelectedIndeterminate"
          @change="toggleLicensesSelected"
          >&nbsp;</b-checkbox
        >
      </template>

      <template #cell(select)="{ item }">
        <b-checkbox v-model="licensesSelected" :value="item.id"
          >&nbsp;</b-checkbox
        >
      </template>

      <template #cell(type)="{ item }">
        <i :class="productDetails[item.type].icon"></i>
        {{ item.type === "edu" ? "Academic" : item.type | capitalize }}

        <b-btn
          v-if="canEdit(item)"
          @click="
            licensesSelected = [];
            configureLicense = item;
          "
          variant="link"
          aria-label="Configure license"
        >
          <i class="icon-settings" />
          <i
            v-if="!isEmptyConfig(item.data)"
            v-b-tooltip.hover="'Configured'"
            class="icon-flag text-danger"
          />
        </b-btn>

        <i
          v-if="isPluginEnabled(item.data)"
          v-b-tooltip.hover="'Stacks Enabled'"
          class="icon-layers text-indigo"
        />

        <b-badge
          class="ml-2"
          variant="secondary"
          v-if="isViewerLicense(item.data)"
          >viewer</b-badge
        >
      </template>

      <template #cell(username)="{ item }">
        {{ item.username || "-" }}
      </template>

      <template #cell(email)="{ item }">
        <span :id="`email-input-${item.id}`">
          <NanLoading v-if="item.isLoading" />

          <form
            v-else-if="
              isEditing && item.type !== 'trial' && item.status !== 'expired'
            "
            class="d-flex align-items-center"
            @submit.prevent
          >
            <input
              v-if="!assignEmails.length || item.showInput"
              v-model="item.email"
              @keyup.enter="assignLicense(item, item.email)"
              class="flex-grow-1 form-control"
              placeholder="Email"
              autocomplete="off"
            />

            <b-select
              v-else
              v-model="item.email"
              :options="getAssignOptions(item)"
              @input="
                email => {
                  if (email === 'other') item.email = '';
                  item.showInput = email === 'other';
                }
              "
            />

            <!-- Assigned but no changes made -->
            <b-btn
              v-if="item.email && item.email === item.using.email"
              @click.stop="assignLicense(item, null)"
              class="ml-2"
              variant="link"
            >
              <span class="screen-reader-text">Delete</span>
              <i class="icon-close"></i>
            </b-btn>
            <!-- Assigned & changes have been made -->
            <b-btn
              v-else-if="item.email !== item.using.email"
              @click.stop="assignLicense(item, item.email)"
              :disabled="isLicenseInvalid(item)"
              class="ml-2"
              variant="link"
            >
              <span class="screen-reader-text">Confirm</span>
              <i class="icon-check"></i>
            </b-btn>
            <!-- Unassigned -->
            <b-btn
              v-else
              @click="assignLicense(item, item.email)"
              :disabled="isLicenseInvalid(item) || !item.email"
              class="ml-2"
              variant="link"
            >
              <span class="screen-reader-text">Add</span>
              <i class="icon-plus"></i>
            </b-btn>
          </form>

          <template v-else>
            <b-badge
              v-if="
                item.using.email &&
                assignEmails.length &&
                !assignEmails.includes(item.using.email)
              "
              >external</b-badge
            >
            {{ item.using.email || "-" }}
            <b-btn
              v-if="item.status === 'pending' && !item.inviteSent"
              @click="resendInvite(item)"
              id="license-resend-invite"
              variant="link"
            >
              <i class="icon-reload"></i>
              <span class="screen-reader-text">Resend Invite</span>
            </b-btn>
            <b-tooltip target="license-resend-invite">
              Re-Send Invite
            </b-tooltip>
          </template>
        </span>
        <b-tooltip
          :show.sync="tooltips[item.id]"
          :target="`email-input-${item.id}`"
          title="Invitation sent"
          triggers
        ></b-tooltip>
      </template>

      <template #cell(status)="{ item }">
        <b-badge :variant="getBadge(item.status)">
          {{ item.status }}
        </b-badge>
      </template>

      <template #cell(expires)="{ item }">
        {{ item.expired_time | date }}
      </template>

      <template #cell(download)="{ item, index }">
        <b-button
          v-if="item.type === 'enterprise'"
          variant="link"
          @click="downloadLicense(item, $event.target)"
          :aria-label="`Download license ${index + 1}`"
          class="licenses-manage__download"
        >
          Download
          <i class="icon-cloud-download ml-2"></i>
        </b-button>
        <span v-else v-b-tooltip.hover="'Enterprise only'">-</span>
      </template>
    </b-table>
    <nav>
      <b-pagination
        v-if="licenseTotalFiltered > perPage"
        v-model="licenseCurrentPage"
        :total-rows="licenseTotalFiltered"
        :per-page="perPage"
        align="center"
        prev-text="Prev"
        next-text="Next"
        hide-goto-end-buttons
      />
    </nav>

    <b-modal
      :visible="!!configureLicense"
      @hide="closeConfigure"
      centered
      hide-header
      hide-footer
    >
      <LicenseConfigure
        v-if="configureLicense"
        include-expiration
        :batch-ids="licensesSelected.slice(1)"
        :license="configureLicense"
        :reset-config="resetConfig"
        @update="onLicenseUpdate"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { email, minLength } from "vuelidate/lib/validators";

import API from "@/api";
import productDetails from "@/data/products";
import { downloadText } from "@/helpers/download";
import LicenseConfigure from "@/components/licenses/LicenseConfigure";

export default {
  components: {
    LicenseConfigure
  },

  mixins: [validationMixin],

  props: {
    licenses: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ""
    },
    addLicense: {
      type: Boolean,
      default: false
    },
    assignEmails: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    productDetails,
    allLicensesSelected: false,
    configureLicense: null,
    isEditing: false,
    licenseCurrentPage: 1,
    licenseFields: [
      { key: "select", label: "" },
      { key: "type", sortable: true },
      { key: "username", sortable: true },
      { key: "email", sortable: true },
      { key: "status", sortable: true, class: "text-center" },
      { key: "expires", sortable: true, class: "text-center" },
      { key: "download", label: "Offline License", class: "text-center" }
    ],
    licensesFilter: "",
    licensesSelected: [],
    licensesSelectedIndeterminate: false,
    licensesTransformed: [],
    licenseTotalFiltered: 0,
    perPage: 50,
    resetConfig: false,
    tooltips: {}
  }),

  computed: {
    ...mapGetters(["isAdmin", "hasPermission"]),

    licenseFieldsComputed() {
      // hide select and download unless admin
      const canDownload = this.hasPermission("license.assign");
      const canEditOrDelete =
        this.hasPermission("license.edit") ||
        this.hasPermission("license.delete");
      const canBatch = canEditOrDelete || canDownload;
      return this.licenseFields.slice(
        canBatch ? 0 : 1,
        canDownload ? undefined : -1
      );
    }
  },

  watch: {
    licensesFilter() {
      this.licenseCurrentPage = 1;
    },

    licensesSelected() {
      this.allLicensesSelected =
        this.licensesSelected.length === this.licensesTransformed.length;
      this.licensesSelectedIndeterminate =
        this.licensesSelected.length > 0 && !this.allLicensesSelected;
    }
  },

  async mounted() {
    this.isEditing = this.$route.query.edit !== undefined;

    this.licensesTransformed = this.licenses
      .map(item => {
        const license = {
          ...item,
          email: item.using.email, // In case the user wants to edit
          username: item.using.name, // In case the user wants to edit
          expires: new Date(item.expired_time).getTime(), // In case the user wants to edit
          inviteSent: false,
          isLoading: false
        };

        // Convert status to user friendly status
        if (item.status === "INUSE") {
          license.status = "accepted";
        } else if (item.status === "WAITING") {
          license.status = "pending";
        }
        license.status = license.status.toLowerCase();

        // create the tooltips map object
        this.$set(this.tooltips, item.id, false);

        return license;
      })
      .sort((a, b) => {
        // Assign order hierarchy and calculate sorting order difference
        const statusSort = ["accepted", "pending", "expired", "unassigned"];
        let total = statusSort.indexOf(a.status) - statusSort.indexOf(b.status);

        // If both values are equal, sort by assigned user's email
        if (total === 0) {
          total = a.using.email < b.using.email ? -1 : 1;
        }

        return total;
      });
    this.licenseTotalFiltered = this.licensesTransformed.length;
  },

  methods: {
    async assignLicense(license, email) {
      if (this.isLicenseInvalid(license)) {
        return this.$notify({
          type: "error",
          title: "Error:",
          text: "The email you tried to assign to is invalid."
        });
      }
      if (email === license.using.email) return;

      email = email || null; // For empty strings, set to null to remove (API only accepts null)

      let reset = null;
      if (
        this.hasPermission("license.edit") &&
        email === null &&
        !this.isEmptyConfig(license.data)
      ) {
        const h = this.$createElement;
        reset = await this.$bvModal.msgBoxConfirm(
          [
            h("div", [
              "Do you want to clear license data?",
              h("pre", JSON.stringify(license.data, 0, 2))
            ])
          ],
          {
            centered: true,
            cancelTitle: "Keep",
            okTitle: "Clear",
            okVariant: "danger",
            title: "Clear License Data",
            headerBgVariant: "danger"
          }
        );

        if (reset === null) return;
      }

      license.isLoading = true;
      const { success } = await API.licenseAssign({
        license: license.id,
        login: email,
        reset
      });
      license.isLoading = false;
      if (!success) return;

      // TODO:
      // Should the API give us an updated license response?
      // If we do get a status update in response, apply it here.
      // Otherwise we can presume it went through and apply it in local state
      if (email) {
        license.using.email = email;
        license.email = email;
        license.inviteSent = true;

        if (license.owner && email === license.owner.email) {
          license.username = license.owner.name;
          license.status = "accepted";
        } else {
          this.tooltips[license.id] = true;
          setTimeout(() => {
            this.tooltips[license.id] = false;
          }, 1000);

          license.status = "pending";
          license.username = null;
        }
      } else {
        license.email = null;
        license.using.email = null;
        license.username = null;
        license.status = "unassigned";
        if (reset) {
          license.data = {};
        }
      }

      const text = !license.email
        ? "unassigned"
        : `assigned to ${license.email}`;
      this.$notify({
        type: "success",
        title: "Success:",
        text: `License ${text} successfully.`
      });
    },

    closeConfigure() {
      this.configureLicense = null;
      this.resetConfig = false;
    },

    batchConfigure() {
      const configureLicense = this.licensesTransformed.find(
        l => l.id == this.licensesSelected[0]
      );
      const configureData = JSON.stringify(configureLicense.data);

      this.resetConfig = false;
      this.licensesTransformed.forEach(license => {
        if (!this.licensesSelected.includes(license.id)) return;
        if (JSON.stringify(license.data) !== configureData) {
          this.resetConfig = true;
        }
      });

      this.configureLicense = configureLicense;
    },

    async batchDelete() {
      const ids = this.licensesSelected;
      const confirm = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to permanently delete ${ids.length} license(s)?`,
        {
          title: "Delete License(s)",
          okVariant: "danger",
          okTitle: "Delete",
          size: "sm",
          centered: true
        }
      );
      if (!confirm) return;

      const res = await API.deleteLicenses(ids);
      if (!res.success) {
        return this.$notify({
          type: "error",
          text: res.error.message
        });
      }

      this.$notify({
        type: "success",
        text: "License(s) deleted"
      });

      this.licensesTransformed = this.licensesTransformed.filter(
        l => !ids.includes(l.id)
      );
      this.licensesSelected = [];
    },

    async batchDownload() {
      const ids = this.licensesSelected;
      const licenses = this.licensesTransformed.filter(l => {
        return ids.includes(l.id) && l.type === "enterprise";
      });

      for (const license of licenses) {
        await this.downloadLicense(license);
      }
    },

    canEdit(license) {
      if (this.hasPermission("license.edit")) return true;
      if (license.type !== "trial") return false;
      const canExtendTrial = this.hasPermission("license.trial-extend");
      const canUnlockTrial = this.hasPermission("license.trial-unlock");
      return canExtendTrial || canUnlockTrial;
    },

    async downloadLicense(license, clickedEl) {
      try {
        const { success, results } = await API.downloadLicense(license.id);
        if (success) {
          const name = /user\.name: (.+)/.exec(results.content)[1];
          downloadText(
            `nanome-license-${name}-${license.id}.txt`,
            results.content
          );
          if (clickedEl) clickedEl.focus();
        }
      } catch (err) {
        console.warn(err);
      }
    },

    getAssignOptions(item) {
      let first = "";
      if (!this.assignEmails.includes(item.email)) {
        first = item.email || "";
      }
      return [first, ...this.assignEmails, "other"];
    },

    getBadge(status) {
      switch (status) {
        case "accepted":
          return "success";

        case "pending":
          return "warning";

        case "expired":
          return "danger";

        default:
          return "secondary";
      }
    },

    isEmptyConfig({ config, extras } = {}) {
      if (!config && !extras) return true;

      const merged = {
        ...config,
        ...extras
      };
      if (!Object.keys(merged).length) return true;

      return false;
    },

    isLicenseInvalid(license) {
      const index = this.licensesTransformed.findIndex(
        item => item.id === license.id
      );

      return this.$v.licensesTransformed.$each[index].$invalid;
    },

    isPluginEnabled(data = {}) {
      if (this.isEmptyConfig(data)) return false;
      return data.extras && data.extras["allow-plugins"];
    },

    isViewerLicense(data = {}) {
      if (this.isEmptyConfig(data)) return false;
      return data.extras && data.extras["viewer-license"];
    },

    onFiltered(filteredItems) {
      this.licenseTotalFiltered = filteredItems.length;
    },

    onLicenseUpdate(updates) {
      const license = this.licensesTransformed.find(
        license => license.id === this.configureLicense.id
      );
      Object.assign(license, updates);

      this.licensesSelected.forEach(id => {
        const license = this.licensesTransformed.find(
          license => license.id === id
        );
        Object.assign(license, updates);
      });
    },

    async resendInvite(license) {
      license.inviteSent = true;
      const { success } = await API.resendInvite(license.id);
      if (!success) return;

      this.tooltips[license.id] = true;
      setTimeout(() => {
        this.tooltips[license.id] = false;
      }, 1000);
    },

    toggleEdit() {
      this.isEditing = !this.isEditing;
      this.licensesTransformed.forEach(item => {
        item.email = item.using.email || null;
      });
    },

    toggleLicensesSelected(checked) {
      this.licensesSelected = checked
        ? this.licensesTransformed.map(item => item.id)
        : [];
    }
  },

  validations: () => ({
    licensesTransformed: {
      $each: {
        email: {
          email,
          min: minLength(6)
        }
      }
    }
  })
};
</script>

<style lang="scss">
.licenses-manage {
  .btn-link:hover {
    text-decoration: none;
  }

  table {
    white-space: nowrap;
  }

  table td {
    vertical-align: middle;
  }

  table button {
    vertical-align: unset;
    padding: 0;
  }

  &__download {
    display: flex;
    align-items: center;
    margin: 0 auto;
  }
}
</style>
